/**
 * Utility functions for targeting and highlighting elements on a page
 */

/**
 * Resolves a selector string that may contain >>> to indicate crossing shadow DOM
 * or component boundaries.
 * 
 * @param {string} selector - The selector to resolve, may contain >>> to indicate boundaries
 * @param {Document} rootDocument - The root document to start from (defaults to top-level document)
 * @returns {Element|null} - The found element or null if not found
 */
export function resolveSelector(selector, rootDocument = null) {
    if (!selector) return null;
    
    // Always start from the top-level document
    const startDocument = window.top ? window.top.document : document;
    
    try {
        if (selector.includes('>>>')) {
            const parts = selector.split('>>>').map(s => s.trim());
            
            let context = startDocument;
            
            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                
                if (i === parts.length - 1) {
                    try {
                        const element = context.querySelector(part);
                        if (!element) {
                            return null;
                        }
                        return element;
                    } catch (e) {
                        return null;
                    }
                } 
                else {
                    try {
                        const container = context.querySelector(part);
                        if (!container) {
                            return null;
                        }
                        
                        if (container.shadowRoot) {
                            context = container.shadowRoot;
                        } else if (container.tagName === 'IFRAME' && container.contentDocument) {
                            context = container.contentDocument;
                        }
                        else {
                            context = container;
                        }
                    } catch (e) {
                        return null;
                    }
                }
            }
            
            return null;
        } 
        else {
            const element = startDocument.querySelector(selector);
            if (!element) {
                return null;
            }
            return element;
        }
    } catch (e) {
        return null;
    }
}

/**
 * Highlights an element with a red outline and scrolls it into view
 * 
 * @param {Element} element - The element to highlight
 */
export function highlightElement(element) {
    if (!element) return;
    
    element.style.outline = '4px solid red';
    element.style['outline-offset'] = '12px';
    element.scrollIntoView({block: 'center'});
    
    if (typeof inspect === 'function') {
        inspect(element);
    }
}

/**
 * Creates a minified JavaScript snippet for finding and highlighting an element
 * using the specified selector. The snippet is suitable for copying and pasting 
 * into a browser console.
 * 
 * @param {string} selector - The selector to target
 * @returns {string} - A minified JavaScript snippet
 */
export function createHighlightSnippet(selector) {
    // This is the code that will be minified
    const code = `
    (function() {
        function resolveSelector(selector) {
            if (!selector) return null;
            
            // Always start from the top-level document
            const startDocument = window.top ? window.top.document : document;
            
            try {
                if (selector.includes('>>>')) {
                    const parts = selector.split('>>>').map(s => s.trim());
                    let context = startDocument;
                    for (let i = 0; i < parts.length; i++) {
                        const part = parts[i];
                        if (i === parts.length - 1) {
                            try {
                                const element = context.querySelector(part);
                                if (!element) {
                                    return null;
                                }
                                return element;
                            } catch (e) {
                                return null;
                            }
                        } else {
                            try {
                                const container = context.querySelector(part);
                                if (!container) {
                                    return null;
                                }
                                if (container.shadowRoot) {
                                    context = container.shadowRoot;
                                } else if (container.tagName === 'IFRAME' && container.contentDocument) {
                                    context = container.contentDocument;
                                } else {
                                    context = container;
                                }
                            } catch (e) {
                                return null;
                            }
                        }
                    }
                    return null;
                } else {
                    const element = startDocument.querySelector(selector);
                    if (!element) {
                        return null;
                    }
                    return element;
                }
            } catch (e) {
                return null;
            }
        }
        
        function highlightElement(element) {
            if (!element) return;
            element.style.outline = '4px solid red';
            element.style['outline-offset'] = '12px';
            element.scrollIntoView({block: 'center'});
            if (typeof inspect === 'function') {
                inspect(element);
            }
        }
        
        try {
            const selector = ${JSON.stringify(selector)};
            const element = resolveSelector(selector);
            if (element) {
                highlightElement(element);
            } else {
                console.log('Could not find element with selector:', selector);
            }
        } catch (e) {
            console.error('Error highlighting element:', e);
        }
    })();
    `;
    
    // Simple minification: remove comments, extra whitespace, and line breaks
    return code
        .replace(/\/\/.*$/gm, '') // Remove single-line comments
        .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments
        .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
        .replace(/^\s+|\s+$/g, ''); // Trim whitespace from beginning and end
}